import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { isSessionExpired } from "@/lib/session-expiration";

export interface WaiterWorkSession {
  id: string;
  restaurant_id: string;
  user_id: string;
  status: 'pending' | 'active' | 'expired' | 'revoked';
  requested_at: string;
  started_at: string | null;
  expires_at: string | null;
  checkin_method: 'code' | 'approval';
  approved_by: string | null;
  device_info: Record<string, unknown> | null;
}

interface UseWaiterWorkSessionReturn {
  session: WaiterWorkSession | null;
  hasActiveSession: boolean;
  isLoading: boolean;
  isPending: boolean;
  remainingMinutes: number | null;
  checkinWithCode: (code: string, sessionType?: "waiter" | "tablet") => Promise<boolean>;
  requestApproval: (sessionType?: "waiter" | "tablet") => Promise<boolean>;
  cancelPendingRequest: () => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useWaiterWorkSession(): UseWaiterWorkSessionReturn {
  const { user } = useAuth();
  const { activeRestaurant } = useRestaurantContext();
  const [session, setSession] = useState<WaiterWorkSession | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const channelIdRef = useRef(Math.random().toString(36).slice(2, 8));

  const getDeviceInfo = useCallback((sessionType: "waiter" | "tablet" = "waiter") => {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      timestamp: new Date().toISOString(),
      sessionType,
    };
  }, []);

  const fetchSession = useCallback(async () => {
    if (!user || !activeRestaurant) {
      setSession(null);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("waiter_work_sessions")
        .select("*")
        .eq("user_id", user.id)
        .eq("restaurant_id", activeRestaurant.id)
        .in("status", ["active", "pending"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      // Verificar se sessão ativa expirou (12h OU midnight+6h Brasília)
      if (data && data.status === "active") {
        if (isSessionExpired(data.expires_at, data.started_at)) {
          await supabase
            .from("waiter_work_sessions")
            .update({ status: "expired", updated_at: new Date().toISOString() })
            .eq("id", data.id);
          setSession(null);
          toast.info("Sessão expirada. Faça check-in novamente para continuar.");
        } else {
          setSession(data as WaiterWorkSession);
        }
      } else {
        setSession(data as WaiterWorkSession | null);
      }
    } catch (error) {
      console.error("Erro ao buscar sessão:", error);
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, [user, activeRestaurant]);

  // Buscar sessão inicial
  useEffect(() => {
    fetchSession();
  }, [fetchSession]);

  // Escutar mudanças em tempo real
  useEffect(() => {
    if (!user || !activeRestaurant) return;

    const channel = supabase
      .channel(`waiter-session-${user.id}-${channelIdRef.current}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "waiter_work_sessions",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const newData = payload.new as WaiterWorkSession;
          
          if (payload.eventType === "UPDATE") {
            if (newData.status === "active" && session?.status === "pending") {
              toast.success("Sessão aprovada! Você pode lançar pedidos.");
            } else if (newData.status === "revoked" || newData.status === "expired") {
              const message = newData.status === "revoked" 
                ? "Sua sessão foi revogada pelo gerente." 
                : "Sua sessão expirou.";
              toast.error(message);
              // NÃO faz logout automático - apenas atualiza o estado
              // O garçom pode fazer novo check-in
            }
          }
          
          fetchSession();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, activeRestaurant, session?.status, fetchSession]);

  // Periodic expiration check (every 60s)
  useEffect(() => {
    if (!session || session.status !== "active") return;

    const checkExpiration = async () => {
      if (isSessionExpired(session.expires_at, session.started_at)) {
        console.log("[WaiterWorkSession] Session expired by periodic check");
        try {
          await supabase
            .from("waiter_work_sessions")
            .update({ status: "expired", updated_at: new Date().toISOString() })
            .eq("id", session.id);
        } catch (e) {
          console.error("Failed to update expired session:", e);
        }
        setSession(null);
        toast.info("Sessão expirada. Faça check-in novamente para continuar.");
      }
    };

    // Check immediately, then every 60 seconds
    checkExpiration();
    const timer = setInterval(checkExpiration, 60_000);
    return () => clearInterval(timer);
  }, [session]);

  // Calcular minutos restantes
  const remainingMinutes = session?.expires_at
    ? Math.max(0, Math.floor((new Date(session.expires_at).getTime() - Date.now()) / 60000))
    : null;

  const checkinWithCode = async (code: string, sessionType: "waiter" | "tablet" = "waiter"): Promise<boolean> => {
    if (!activeRestaurant) {
      toast.error("Nenhum restaurante selecionado");
      return false;
    }

    try {
      const { data, error } = await supabase.rpc("waiter_checkin_with_code", {
        p_restaurant_id: activeRestaurant.id,
        p_code: code.toUpperCase(),
        p_device_info: getDeviceInfo(sessionType),
      });

      if (error) {
        if (error.message.includes("inválido")) {
          toast.error("Código inválido ou expirado");
        } else {
          toast.error("Erro ao fazer check-in");
        }
        return false;
      }

      toast.success("Check-in realizado com sucesso!");
      await fetchSession();
      return true;
    } catch (error) {
      console.error("Erro no check-in:", error);
      toast.error("Erro ao fazer check-in");
      return false;
    }
  };

  const requestApproval = async (sessionType: "waiter" | "tablet" = "waiter"): Promise<boolean> => {
    if (!user || !activeRestaurant) {
      toast.error("Usuário ou restaurante não encontrado");
      return false;
    }

    try {
      const { error } = await supabase.from("waiter_work_sessions").insert({
        restaurant_id: activeRestaurant.id,
        user_id: user.id,
        status: "pending",
        checkin_method: "approval",
        device_info: getDeviceInfo(sessionType),
      });

      if (error) throw error;

      toast.success("Solicitação enviada! Aguarde aprovação.");
      await fetchSession();
      return true;
    } catch (error) {
      console.error("Erro ao solicitar aprovação:", error);
      toast.error("Erro ao enviar solicitação");
      return false;
    }
  };

  const cancelPendingRequest = async (): Promise<boolean> => {
    if (!session || session.status !== "pending") return false;

    try {
      const { error } = await supabase
        .from("waiter_work_sessions")
        .update({ status: "expired", updated_at: new Date().toISOString() })
        .eq("id", session.id);

      if (error) throw error;

      toast.success("Solicitação cancelada");
      await fetchSession();
      return true;
    } catch (error) {
      console.error("Erro ao cancelar solicitação:", error);
      toast.error("Erro ao cancelar");
      return false;
    }
  };

  return {
    session,
    hasActiveSession: session?.status === "active",
    isLoading,
    isPending: session?.status === "pending",
    remainingMinutes,
    checkinWithCode,
    requestApproval,
    cancelPendingRequest,
    refetch: fetchSession,
  };
}
